class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.bindPurchaseOptions();
  }

  getSection() {
    return document.getElementById(`shopify-section-${this.dataset.sectionId}`);
  }

  bindPurchaseOptions() {
    const section = this.getSection();
    if (!section) return;

    if (section._purchaseOptionsAbort) {
      section._purchaseOptionsAbort.abort();
    }
    section._purchaseOptionsAbort = new AbortController();
    const { signal } = section._purchaseOptionsAbort;

    this.sellingPlanInputs = section.querySelectorAll(
      ".subscription-widget__selling-plan-input",
    );
    section.querySelectorAll("[data-purchase-type]").forEach((radio) => {
      radio.addEventListener(
        "change",
        () => {
          if (radio.dataset.purchaseType === "subscription") {
            this.updatePlanDisplayForCard(
              radio.closest("[data-subscription-card]"),
              section,
            );
          } else {
            this.updateAddToCartPrice(section);
          }
          this.updateSubscriptionDetails(section);
          this.updateSellingPlan(section);
        },
        { signal },
      );
    });

    section.querySelectorAll("[data-subscription-card]").forEach((card) => {
      card.addEventListener(
        "click",
        (event) => {
          if (event.target.closest("select")) return;

          const radio = card.querySelector(".subscription-widget__radio");
          if (!radio || radio.disabled || radio.checked) return;

          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        },
        { signal },
      );
    });

    section.querySelectorAll("[data-subscription-plan-select]").forEach((planSelect) => {
      planSelect.addEventListener(
        "change",
        () => {
          const card = planSelect.closest("[data-subscription-card]");
          this.updatePlanDisplayForCard(card, section);
          this.updateSellingPlan(section);
        },
        { signal },
      );
    });

    section
      .querySelectorAll("[data-subscription-group]")
      .forEach((card) => {
        this.updatePlanDisplayForCard(card, section);
      });
    this.updateSubscriptionDetails(section);
    this.updateSellingPlan(section);
  }

  updateSubscriptionDetails(section = this.getSection()) {
    section?.querySelectorAll("[data-subscription-card]").forEach((card) => {
      const radio = card.querySelector(".subscription-widget__radio");
      const details = card.querySelector("[data-subscription-details]");
      if (!radio || !details) return;

      const isSelected = radio.checked;
      details.hidden = !isSelected;
      radio.setAttribute("aria-expanded", String(isSelected));
    });
  }

  getPurchaseWidget(section = this.getSection()) {
    return section?.querySelector("subscription-widget");
  }

  updatePlanDisplayForCard(card, section = this.getSection()) {
    if (!card || !section) return;

    const planSelect = card.querySelector("[data-subscription-plan-select]");
    if (!planSelect) return;

    const selectedOption = planSelect.selectedOptions[0];
    if (!selectedOption) return;

    const widget = this.getPurchaseWidget(section);
    const showPrice = widget?.dataset.showPrice === "true";
    const salePrice = card.querySelector("[data-subscription-sale-price]");
    const comparePrice = card.querySelector("[data-subscription-compare-price]");
    const discountBadge = card.querySelector("[data-subscription-discount-badge]");

    if (discountBadge) {
      const discount = Number(selectedOption.dataset.discount || 0);
      if (discount > 0 && selectedOption.dataset.discountLabel) {
        discountBadge.textContent = selectedOption.dataset.discountLabel;
        discountBadge.hidden = false;
      } else {
        discountBadge.hidden = true;
      }
    }

    if (showPrice) {
      if (salePrice && selectedOption.dataset.price) {
        salePrice.textContent = selectedOption.dataset.price;
      }

      if (comparePrice) {
        if (selectedOption.dataset.comparePrice) {
          comparePrice.textContent = selectedOption.dataset.comparePrice;
          comparePrice.hidden = false;
        } else {
          comparePrice.hidden = true;
        }
      }
    }

    if (card.querySelector(".subscription-widget__radio")?.checked) {
      this.updateAddToCartPrice(section, selectedOption.dataset.priceCents);
    }
  }

  formatMoney(cents) {
    const format = window.themeGlobalVariables?.settings?.money_format || "${{ amount }}";
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents || 0) / 100));

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  updateAddToCartPrice(section = this.getSection(), priceCents) {
    const forms = [
      document.getElementById(this.dataset.formId),
      document.getElementById(`sticky-addcart-form-${this.dataset.sectionId}`),
    ].filter(Boolean);

    forms.forEach((form) => {
      const priceElement = form.querySelector(".total-price__detail");
      if (!priceElement) return;

      if (!priceElement.dataset.originalTotalPrice) {
        priceElement.dataset.originalTotalPrice = priceElement.dataset.totalPrice || "";
        priceElement.dataset.originalPriceText = priceElement.textContent.trim();
      }

      if (!priceCents) {
        const originalTotalPrice = priceElement.dataset.originalTotalPrice;
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        priceElement.dataset.totalPrice = originalTotalPrice;
        priceElement.textContent = originalTotalPrice
          ? this.formatMoney(Number(originalTotalPrice) * quantity)
          : priceElement.dataset.originalPriceText;
        return;
      }

      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      const totalPrice = Number(priceCents) * quantity;
      priceElement.dataset.totalPrice = priceCents;
      priceElement.textContent = quantity === 1 ? this.formatMoney(priceCents) : this.formatMoney(totalPrice);
    });
  }

  updateSellingPlan(section = this.getSection()) {
    const selected = section?.querySelector('[name^="purchase_option_"]:checked');
    if (!selected || !this.sellingPlanInputs?.length) return;

    if (selected.dataset.purchaseType === "one-time") {
      this.sellingPlanInputs.forEach((input) => {
        input.value = "";
      });
      return;
    }

    const planSelect = selected
      .closest("[data-subscription-card]")
      ?.querySelector("[data-subscription-plan-select]");
    if (planSelect) {
      this.sellingPlanInputs.forEach((input) => {
        input.value = planSelect.value;
      });
    }
  }
}

customElements.define("subscription-widget", SubscriptionWidget);
